import { useEffect, useLayoutEffect, useRef, useState, type DragEvent } from 'react';
import { useStore } from '../core/store';
import { layoutPreview, snapPreview, useApp, useSession, useUi } from './context';
import { isCompactCanvas, patchUi, ui } from './ui';
import { onLinkRequest } from '../editor/setup';
import { applyTheme, resolveTheme } from '../platform/theme';
import { markCanvasMounted, Window } from '../ui/Window';
import { BrandCorner } from '../ui/Brand';
import { Intro } from '../ui/Intro';
import { DevTools, DEV_TOOLS } from '../ui/DevTools';
import { LayoutPopover } from '../ui/Layout';
import type { WindowLayout } from '../core/layouts';
import { Palette } from '../ui/Palette';
import { CloudDialog } from '../ui/CloudDialog';
import { WorkspaceOverview } from '../ui/Workspaces';
import { ContextMenu } from '../ui/ContextMenu';
import { TooltipLayer } from '../ui/Tooltip';
import { isMac, isMod } from '../ui/keys';

/**
 * The canvas. The browser window becomes a quiet surface on which MyTXT
 * windows live — no desktop, no dock, just room. On a small screen the
 * spatial metaphor gives way: the focused window simply is the screen.
 */

export function App() {
  const app = useApp();
  const ready = useUi((s) => s.ready);
  const theme = useUi((s) => s.theme);
  const typing = useUi((s) => s.typing);
  const intro = useUi((s) => s.intro);
  const compact = useUi((s) => s.compact);
  const windows = useSession((s) => s.windows);
  const firstPage = useSession((s) => s.windows.length === 1 && s.windows[0].docIds.length === 0);
  const [dropping, setDropping] = useState(false);
  const opening = intro !== 'none';

  useTheme(theme);
  useShortcuts();

  useEffect(() => {
    markCanvasMounted();
    onLinkRequest((view) => {
      if (view !== app.view()) return;
      patchUi({ linkEditor: ui.get().contextShown ? 'context' : 'primary', typing: false });
    });
  }, [app]);

  // Chrome recedes while typing and returns with the pointer or with focus elsewhere.
  useEffect(() => {
    if (!typing) return;
    let origin: { x: number; y: number } | null = null;
    const move = (e: PointerEvent) => {
      origin ??= { x: e.clientX, y: e.clientY };
      if (Math.hypot(e.clientX - origin.x, e.clientY - origin.y) > 6) patchUi({ typing: false });
    };
    const focus = (e: FocusEvent) => !(e.target as Element).closest?.('.cm-editor') && patchUi({ typing: false });
    const press = () => patchUi({ typing: false });
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerdown', press);
    document.addEventListener('focusin', focus);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerdown', press);
      document.removeEventListener('focusin', focus);
    };
  }, [typing]);

  // The canvas follows the viewport: compact below a size, and windows re-fit when it changes.
  useLayoutEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const measure = () => {
      patchUi({ compact: isCompactCanvas(window.innerWidth, window.innerHeight) });
      clearTimeout(timer);
      timer = setTimeout(() => app.fitToCanvas(), 120);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', measure);
    };
  }, [app]);

  // Never let the page go away while a draft has not reached storage yet.
  useEffect(() => {
    const before = (e: BeforeUnloadEvent) => {
      if (!app.hasUnprotectedWork()) return;
      app.flush();
      e.preventDefault();
    };
    const hide = () => document.visibilityState === 'hidden' && app.flush();
    window.addEventListener('beforeunload', before);
    document.addEventListener('visibilitychange', hide);
    window.addEventListener('pagehide', app.flush);
    return () => {
      window.removeEventListener('beforeunload', before);
      document.removeEventListener('visibilitychange', hide);
      window.removeEventListener('pagehide', app.flush);
    };
  }, [app]);

  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes('Files');
  const onCanvas = (e: { target: EventTarget; currentTarget: EventTarget }) => e.target === e.currentTarget || (e.target as HTMLElement).classList?.contains('canvas-layer');

  return (
    <div
      className="canvas"
      data-ready={ready || undefined}
      data-intro={intro}
      data-compact={compact || undefined}
      data-typing={typing || undefined}
      data-first-page={firstPage || undefined}
      data-dropping={dropping || undefined}
      onContextMenu={(e) => {
        if (!onCanvas(e)) return;
        e.preventDefault();
        patchUi({ contextMenu: { x: e.clientX, y: e.clientY, target: { kind: 'canvas' }, returnFocus: document.activeElement as HTMLElement | null } });
      }}
      onDragEnter={(e) => hasFiles(e) && onCanvas(e) && setDropping(true)}
      onDragOver={(e) => {
        if (!hasFiles(e)) return;
        // A window under the pointer has taken the drag; the canvas outline steps aside.
        if (e.defaultPrevented) return setDropping(false);
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setDropping(true);
      }}
      onDragLeave={(e) => !e.currentTarget.contains(e.relatedTarget as Node) && setDropping(false)}
      onDrop={(e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        setDropping(false);
        app.openDropped(e.dataTransfer, compact ? undefined : { point: { x: e.clientX, y: e.clientY } });
      }}
    >
      {!compact && <BrandCorner hidden={opening} />}
      <div className="canvas-layer">
        <SnapPreview />
        <LayoutPreview />
        {windows.map((w) => (
          <Window key={w.id} id={w.id} />
        ))}
      </div>
      {opening && <Intro mode={intro} />}
      <Palette />
      <WorkspaceOverview />
      <CloudDialog />
      <ContextMenu />
      <LayoutPopover />
      {DEV_TOOLS && <DevTools />}
      <TooltipLayer />
    </div>
  );
}

function SnapPreview() {
  const preview = useStore(snapPreview, (p) => p);
  const ref = useRef<HTMLDivElement>(null);
  const [last, setLast] = useState(preview);
  if (preview && preview !== last) setLast(preview);
  const shown = preview ?? last;
  return (
    <div
      ref={ref}
      className="snap-preview"
      data-on={!!preview || undefined}
      aria-hidden="true"
      style={shown ? { transform: `translate3d(${shown.rect.x}px, ${shown.rect.y}px, 0)`, width: shown.rect.w, height: shown.rect.h } : undefined}
    />
  );
}

/** Outlines of where windows would go for the layout being considered. */
function LayoutPreview() {
  const rects = useStore(layoutPreview, (p) => p);
  if (!rects) return null;
  return (
    <>
      {rects.map((r, i) => (
        <div
          key={i}
          className="snap-preview layout-ghost"
          data-on
          aria-hidden="true"
          style={{ transform: `translate3d(${r.x}px, ${r.y}px, 0)`, width: r.w, height: r.h, animationDelay: `${i * 18}ms` }}
        />
      ))}
    </>
  );
}

function useTheme(theme: ReturnType<typeof ui.get>['theme']) {
  const first = useRef(true);
  useEffect(() => {
    const media = matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      const resolved = resolveTheme(theme, media.matches);
      patchUi({ resolvedTheme: resolved });
      applyTheme(resolved, !first.current);
      first.current = false;
    };
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [theme]);
}

/** Application shortcuts. Formatting shortcuts live in the editor keymap. */
function useShortcuts() {
  const app = useApp();
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (ui.get().dialog) return;
      const k = e.key.toLowerCase();
      const code = e.code;
      if (e.ctrlKey && e.key === 'Tab') {
        e.preventDefault();
        app.cycle(e.shiftKey ? -1 : 1);
        return;
      }
      const layout = layoutShortcut(e);
      if (layout && !ui.get().compact) {
        e.preventDefault();
        const id = app.session.get().focusedId;
        if (layout === 'restore') {
          if (!app.undoLayout()) {
            const w = app.session.get().windows.find((x) => x.id === id);
            if (w?.zone) app.layoutWindow(id, w.zone);
          }
        } else if (layout === 'max') app.toggleMaximize(id);
        else app.layoutWindow(id, layout);
        return;
      }
      if (!isMod(e)) {
        // Typing with nothing focused goes to the focused window's editor.
        const target = e.target as HTMLElement;
        if (target === document.body && !e.altKey && !e.ctrlKey && (e.key.length === 1 || e.key === 'Process') && !ui.get().palette) app.view()?.focus();
        return;
      }
      const hasDoc = !!app.session.get().docs.length && !!app.session.get().windows.find((w) => w.id === app.session.get().focusedId)?.activeId;
      if (k === 'k' && !e.shiftKey && !e.altKey) {
        if (e.defaultPrevented) return;
        e.preventDefault();
        patchUi({ palette: !ui.get().palette, contextMenu: null });
      } else if ((k === 'n' || code === 'KeyN') && !e.shiftKey) {
        e.preventDefault();
        app.newDocument(e.altKey ? 'txt' : 'md');
      } else if (k === 'o' && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        app.openFiles();
      } else if (k === 's' && hasDoc && !e.altKey) {
        e.preventDefault();
        if (e.shiftKey) app.saveAs();
        else app.save();
      } else if (k === 'w' && hasDoc && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        app.close();
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [app]);
}

/**
 * Window layouts from the keyboard: Control-Option (Ctrl+Alt) with the arrow
 * keys for halves, Return to maximise, Backspace to restore — the conventions
 * of desktop window managers, which no browser or editor binding uses. Letter
 * shortcuts (centre, quarters, thirds) are offered only on macOS, where
 * Control-Option does not type characters as AltGr does elsewhere.
 */
export function layoutShortcut(e: Pick<KeyboardEvent, 'ctrlKey' | 'altKey' | 'metaKey' | 'shiftKey' | 'code'>): WindowLayout | 'max' | 'restore' | null {
  if (!e.ctrlKey || !e.altKey || e.metaKey || e.shiftKey) return null;
  const common: Record<string, WindowLayout | 'max' | 'restore'> = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'top', ArrowDown: 'bottom', Enter: 'max', Backspace: 'restore' };
  if (common[e.code]) return common[e.code];
  if (!isMac) return null;
  const mac: Record<string, WindowLayout> = { KeyC: 'center', KeyU: 'tl', KeyI: 'tr', KeyJ: 'bl', KeyK: 'br', KeyD: 'third1', KeyF: 'third2', KeyG: 'third3', KeyE: 'almost' };
  return mac[e.code] ?? null;
}
